'use client'

import { useEffect, useState } from 'react'
import { useRouter } from 'next/navigation'
import { useSession, useSignIn } from '@clerk/nextjs'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { OnboardingLayout } from '@/features/onboarding'
import {
  emailSignInSchema,
  otpSchema,
  phoneSchema,
  type EmailSignInFormValues,
  type OtpFormValues,
  type PhoneFormValues,
} from '@/features/auth/schemas/auth.schema'
import { InvestorForgotPasswordDialog } from '@/features/auth/components/investor-forgot-password-dialog'
import { useFinalizeAsRole } from '@/features/auth/lib/use-finalize-as-role'
import { roleFromSessionClaims } from '@/features/auth/lib/session'
import { Button } from '@/components/ui/button'
import { Field, FieldDescription, FieldGroup, FieldLabel, FieldError } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { PasswordInput } from '@/components/patterns/passwordInput'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'

const RESEND_COOLDOWN_SECONDS = 30

export function LoginView() {
  const router = useRouter()
  const { signIn } = useSignIn()
  const finalizeAs = useFinalizeAsRole()
  const { isLoaded: sessionLoaded, session: existingSession } = useSession()
  const [otpSent, setOtpSent] = useState(false)
  const [loading, setLoading] = useState(false)
  const [resending, setResending] = useState(false)
  const [formError, setFormError] = useState<string | null>(null)
  const [otpInfoMessage, setOtpInfoMessage] = useState<string | null>(null)
  // Starts counting the moment a code is sent — mirrors `reset-password-code-step.tsx`'s
  // resend pattern exactly, so the two OTP surfaces in this app behave identically.
  const [cooldown, setCooldown] = useState(0)
  // Same latch-and-blank-render pattern as `/team/login` (`team-login-view.tsx`) — see its
  // comment for why this stays purely client-side rather than a Server Component check.
  const [authCheck, setAuthCheck] = useState<'pending' | 'redirecting' | 'show-form'>('pending')

  useEffect(() => {
    if (!sessionLoaded) return
    const role = roleFromSessionClaims(existingSession?.lastActiveToken?.jwt?.claims)
    if (role === 'investor') {
      setAuthCheck('redirecting')
      router.replace('/dashboard')
      return
    }
    setAuthCheck('show-form')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionLoaded])

  useEffect(() => {
    if (cooldown === 0) return
    const timer = setInterval(() => setCooldown((seconds) => Math.max(0, seconds - 1)), 1000)
    return () => clearInterval(timer)
  }, [cooldown])

  // The investor shell layout's own server-side `auth.protect()` check is the real authority
  // on role — `finalizeAs('investor', ...)` here just rejects a successful sign-in that turns
  // out not to be an investor account (e.g. a staff/admin account used on this form by
  // mistake) instead of blindly navigating any completed sign-in to `/dashboard`.
  const goToDashboard = () => finalizeAs('investor', setFormError, '/login')

  const phoneForm = useForm<PhoneFormValues>({
    resolver: zodResolver(phoneSchema),
    mode: 'onTouched',
    defaultValues: { phone: '' },
  })
  const otpForm = useForm<OtpFormValues>({
    resolver: zodResolver(otpSchema),
    mode: 'onTouched',
    defaultValues: { otp: '' },
  })
  const emailForm = useForm<EmailSignInFormValues>({
    resolver: zodResolver(emailSignInSchema),
    mode: 'onTouched',
    defaultValues: { email: '', password: '' },
  })

  const phone = phoneForm.watch('phone')

  const handleSendOtp = phoneForm.handleSubmit(async (values) => {
    setLoading(true)
    setFormError(null)
    try {
      const phoneNumber = `+91${values.phone}`
      const { error } = await signIn.phoneCode.sendCode({ phoneNumber })
      if (error) {
        // This account may not exist, or phone sign-in may not be enabled for it — either
        // way, don't reveal which (same "same message either way" policy as password reset).
        setFormError('We could not send a code to that number. Check it and try again.')
        return
      }
      setOtpSent(true)
      setCooldown(RESEND_COOLDOWN_SECONDS)
    } finally {
      setLoading(false)
    }
  })

  const handleResendOtp = async () => {
    setResending(true)
    setFormError(null)
    setOtpInfoMessage(null)
    try {
      const phoneNumber = `+91${phone}`
      // Clerk has no separate "resend" call — sending the code again is the resend (same
      // approach as `reset-password-code-step.tsx`).
      const { error } = await signIn.phoneCode.sendCode({ phoneNumber })
      if (error) {
        setFormError('We could not send a new code. Please try again.')
        return
      }
      otpForm.reset({ otp: '' })
      setCooldown(RESEND_COOLDOWN_SECONDS)
      setOtpInfoMessage("We've sent a new code.")
    } finally {
      setResending(false)
    }
  }

  const handleVerifyOtp = otpForm.handleSubmit(async (values) => {
    setLoading(true)
    setFormError(null)
    try {
      const { error } = await signIn.phoneCode.verifyCode({ code: values.otp })
      if (error) {
        setFormError('That code did not work. Please try again.')
        return
      }
      if (signIn.status === 'complete') {
        await goToDashboard()
        return
      }
      setFormError('That code did not work. Please try again.')
    } finally {
      setLoading(false)
    }
  })

  const handleEmailSignIn = emailForm.handleSubmit(async (values) => {
    setLoading(true)
    setFormError(null)
    try {
      const { error } = await signIn.password({
        emailAddress: values.email,
        password: values.password,
      })
      if (error) {
        setFormError('That email or password is incorrect.')
        return
      }
      if (signIn.status === 'complete') {
        await goToDashboard()
        return
      }
      setFormError('Sign-in did not complete. Please try again.')
    } finally {
      setLoading(false)
    }
  })

  if (authCheck !== 'show-form') {
    return null
  }

  return (
    <OnboardingLayout
      title="Welcome back"
      description="Sign in to your Nivex account"
      width="sm"
      brandPanel
      below={
        <>
          <FieldDescription className="text-center">
            New here?{' '}
            <button
              type="button"
              onClick={() => router.push('/choose-service')}
              className="text-primary font-semibold underline-offset-4 hover:underline"
            >
              Get started
            </button>
          </FieldDescription>
        </>
      }
    >
      <Tabs
        defaultValue="phone"
        onValueChange={() => {
          setOtpSent(false)
          otpForm.reset({ otp: '' })
          setFormError(null)
          setOtpInfoMessage(null)
          setCooldown(0)
        }}
      >
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="phone">Phone</TabsTrigger>
          <TabsTrigger value="email">Email</TabsTrigger>
        </TabsList>

        <TabsContent value="phone" className="mt-5">
          {!otpSent ? (
            <FieldGroup>
              <Field>
                <FieldLabel htmlFor="phone">Mobile number</FieldLabel>
                <div className="flex gap-2">
                  <span className="bg-muted text-muted-foreground flex h-9 shrink-0 items-center rounded-md border px-3 text-sm font-medium">
                    🇮🇳 +91
                  </span>
                  <Input
                    id="phone"
                    type="tel"
                    inputMode="numeric"
                    maxLength={10}
                    placeholder="Enter 10-digit number"
                    aria-invalid={!!phoneForm.formState.errors.phone}
                    {...phoneForm.register('phone', {
                      onChange: (e) => {
                        e.target.value = e.target.value.replace(/\D/g, '').slice(0, 10)
                      },
                    })}
                  />
                </div>
                <FieldError errors={[phoneForm.formState.errors.phone]} />
              </Field>
              {formError && (
                <FieldDescription className="text-red-600">{formError}</FieldDescription>
              )}
              <Field>
                <Button type="button" onClick={handleSendOtp} disabled={loading}>
                  {loading ? 'Sending…' : 'Send OTP'}
                </Button>
              </Field>
            </FieldGroup>
          ) : (
            <FieldGroup>
              <Field>
                <div className="flex items-center justify-between">
                  <FieldLabel htmlFor="otp">Enter the 6-digit code</FieldLabel>
                  <button
                    type="button"
                    onClick={() => {
                      setOtpSent(false)
                      setOtpInfoMessage(null)
                      setCooldown(0)
                    }}
                    className="text-primary text-sm font-semibold underline-offset-4 hover:underline"
                  >
                    Change
                  </button>
                </div>
                <Input
                  id="otp"
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  placeholder="••••••"
                  className="text-center text-lg font-semibold tracking-[8px]"
                  aria-invalid={!!otpForm.formState.errors.otp}
                  {...otpForm.register('otp', {
                    onChange: (e) => {
                      e.target.value = e.target.value.replace(/\D/g, '').slice(0, 6)
                    },
                  })}
                />
                <FieldError errors={[otpForm.formState.errors.otp]} />
                <FieldDescription>
                  We&apos;ve sent a verification code to +91 {phone}.
                </FieldDescription>
              </Field>
              {formError && (
                <FieldDescription className="text-red-600">{formError}</FieldDescription>
              )}
              {otpInfoMessage && <FieldDescription>{otpInfoMessage}</FieldDescription>}
              <Field>
                <Button type="button" onClick={handleVerifyOtp} disabled={loading || resending}>
                  {loading ? 'Verifying…' : 'Verify OTP'}
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  className="w-full"
                  onClick={handleResendOtp}
                  disabled={resending || loading || cooldown > 0}
                >
                  {resending
                    ? 'Sending…'
                    : cooldown > 0
                      ? `Resend code in ${cooldown}s`
                      : "Didn't get a code? Resend"}
                </Button>
              </Field>
            </FieldGroup>
          )}
        </TabsContent>

        <TabsContent value="email" className="mt-5">
          <FieldGroup>
            <Field>
              <FieldLabel htmlFor="email">Email address</FieldLabel>
              <Input
                id="email"
                type="email"
                placeholder="you@example.com"
                aria-invalid={!!emailForm.formState.errors.email}
                {...emailForm.register('email')}
              />
              <FieldError errors={[emailForm.formState.errors.email]} />
            </Field>
            <Field>
              <div className="flex items-center">
                <FieldLabel htmlFor="password">Password</FieldLabel>
                <InvestorForgotPasswordDialog initialEmail={emailForm.watch('email')} />
              </div>
              <PasswordInput
                id="password"
                placeholder="Enter your password"
                aria-invalid={!!emailForm.formState.errors.password}
                {...emailForm.register('password')}
              />
              <FieldError errors={[emailForm.formState.errors.password]} />
            </Field>
            {formError && <FieldDescription className="text-red-600">{formError}</FieldDescription>}
            <Field>
              <Button type="button" onClick={handleEmailSignIn} disabled={loading}>
                {loading ? 'Signing in…' : 'Sign In'}
              </Button>
            </Field>
          </FieldGroup>
        </TabsContent>
      </Tabs>
    </OnboardingLayout>
  )
}
